import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getAirlineStats } from '../../../api/endpoints/airlineStats';
import type { ProviderAirlineStats } from '../../../api/types/airlineStats';
import { AIRLINE_STATS_API_KEY } from '../../../config/env';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';

const NUM = (n: number) => n.toLocaleString('tr-TR');

/**
 * "Havayolu İstatistikleri" — `GET /api/airlinestats` (see `api/types/airlineStats.ts`).
 * Bespoke API-key GET, not the session model; the backend insists on `yyyy-MM-dd`, so the
 * DatePickers are formatted that way before the call. Plain MUI table + client-side SheetJS
 * export, consistent with the sales-report pages.
 */
export default function AirlineStatsPage() {
  const [start, setStart] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [end, setEnd] = useState<Dayjs | null>(dayjs());
  const [provider, setProvider] = useState('');

  const stats = useMutation({
    mutationFn: () =>
      getAirlineStats({
        startDate: (start ?? dayjs()).format('YYYY-MM-DD'),
        endDate: (end ?? dayjs()).format('YYYY-MM-DD'),
        provider: provider.trim() || undefined,
      }),
  });

  if (!AIRLINE_STATS_API_KEY) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Havayolu İstatistikleri
        </Typography>
        <Alert severity="warning">
          Havayolu istatistikleri API anahtarı yapılandırılmamış (REACT_APP_AIRLINE_STATS_API_KEY).
        </Alert>
      </Box>
    );
  }

  const rows: ProviderAirlineStats[] = stats.data?.providers ?? [];
  const totals = rows.reduce(
    (acc, r) => ({
      searchCount: acc.searchCount + (r.searchCount ?? 0),
      reservationCount: acc.reservationCount + (r.reservationCount ?? 0),
      ticketCount: acc.ticketCount + (r.ticketCount ?? 0),
    }),
    { searchCount: 0, reservationCount: 0, ticketCount: 0 }
  );

  const invalid = !start || !end || !start.isValid() || !end.isValid() || end.diff(start, 'day') < 0;

  const exportExcel = async () => {
    // Dynamic import keeps SheetJS (~400 KB) out of the main bundle — same pattern as
    // the sales-report pages (components/report/DynamicReportTable).
    const XLSX = await import('xlsx');
    const sheet = XLSX.utils.json_to_sheet(
      rows.map((r) => ({
        Sağlayıcı: r.provider,
        Arama: r.searchCount,
        Rezervasyon: r.reservationCount,
        Bilet: r.ticketCount,
      }))
    );
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Havayolu İstatistikleri');
    XLSX.writeFile(book, 'havayolu-istatistikleri.xlsx');
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Havayolu İstatistikleri
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', alignItems: { md: 'center' } }}>
          <DatePicker label="Başlangıç" value={start} onChange={setStart} slotProps={{ textField: { size: 'small' } }} />
          <DatePicker label="Bitiş" value={end} onChange={setEnd} slotProps={{ textField: { size: 'small' } }} />
          <TextField
            size="small"
            label="Sağlayıcı (opsiyonel)"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
          />
          <Button variant="contained" onClick={() => stats.mutate()} disabled={stats.isPending || invalid}>
            {stats.isPending ? 'Getiriliyor…' : 'Uygula'}
          </Button>
        </Stack>
        <ApiErrorAlert error={stats.error} sx={{ mt: 2 }} />
      </Paper>

      {stats.isPending && <CircularProgress size={24} />}

      {stats.isSuccess && (
        <Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {rows.length} sağlayıcı
              {stats.data?.startDate && stats.data?.endDate
                ? ` · ${stats.data.startDate} – ${stats.data.endDate}`
                : ''}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportExcel}
              disabled={rows.length === 0}
            >
              Excel İndir
            </Button>
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Sağlayıcı</TableCell>
                  <TableCell align="right">Arama</TableCell>
                  <TableCell align="right">Rezervasyon</TableCell>
                  <TableCell align="right">Bilet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        Bu tarih aralığında kayıt bulunamadı.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.provider} hover>
                    <TableCell>{r.provider}</TableCell>
                    <TableCell align="right">{NUM(r.searchCount ?? 0)}</TableCell>
                    <TableCell align="right">{NUM(r.reservationCount ?? 0)}</TableCell>
                    <TableCell align="right">{NUM(r.ticketCount ?? 0)}</TableCell>
                  </TableRow>
                ))}
                {rows.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Toplam</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {NUM(totals.searchCount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {NUM(totals.reservationCount)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {NUM(totals.ticketCount)}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
